define('presentation/settings/billing/plans/viewModels/removePlanViewModel',[
    'i18next',
    'businessServices/router/router',
    'businessServices/state/modelStateObserver',
    'common/collections/collectionSorter',
    'common/promises/promiseFactory',
    'constants/billingPackageTypeConstants',
    'constants/removePlanFeedbackConstants',
    'entities/planRemovedFeedback',
    'presentation/common/modal',
    'presentation/settings/billing/plans/facades/removePlanFacade',
    'presentation/settings/billing/plans/validators/removePlanValidator',
    'presentation/settings/billing/plans/viewModels/removePlanConfirmationModalViewModel',
    'settings/navigationConfiguration'
], function () {
    return function () {
        let self = this;

        const ActionModalViewModelConstructor = require('presentation/common/actionModal/viewModels/actionModalViewModel');
        const _actionModal = new ActionModalViewModelConstructor();
        
        const CollectionSorterConstructor = require('common/collections/collectionSorter');
        const _collectionSorter = new CollectionSorterConstructor();

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const ModelStateObserverConstructor = require('businessServices/state/modelStateObserver');
        const RemovePlanFeedbackEntity = require('entities/planRemovedFeedback');
        const RemovePlanConfirmationModalViewModelConstructor = require('presentation/settings/billing/plans/viewModels/removePlanConfirmationModalViewModel');

        const _i18n = require('i18next');
        const _navigationConfiguration = require('settings/navigationConfiguration');
        const _router = require('businessServices/router/router');

        const _billingPackageTypes = require('constants/billingPackageTypeConstants');
        const _removePlanFeedbackConstants = require('constants/removePlanFeedbackConstants');
        const _removePlanFeedbackReasons = _removePlanFeedbackConstants.reasons;
        const _noLongerNeededReasons = _removePlanFeedbackConstants.noLongerNeededReasons;
        const _tooExpensiveReasons = _removePlanFeedbackConstants.tooExpensiveReasons;

        let _validator = null;
        let _facade = null;
        let _modalService = null;

        const DROPDOWN_OPTION_YES = {
            id: "Yes",
            description: _i18n.t('yes')
        };
        const DROPDOWN_OPTION_NO = {
            id: "No",
            description: _i18n.t('no')
        };
        const YES_NO_DROPDOWN_OPTIONS = [DROPDOWN_OPTION_NO, DROPDOWN_OPTION_YES];
        const OTHER_OPTION = 'Other';

        const _buildRatingOptions = () => {
            return Array.from(
                { length: 10 },
                (_, index) =>  {
                    return {
                        id: index + 1,
                        isSelected: ko.observable(false)
                    };
                });
        };

        const _formatFeedbackReasons = (reasons) => {
            let formattedReasons = Object.values(reasons).map(reason => ({
                text: reason.description,
                value: reason.id
            }));

            _collectionSorter.sort(formattedReasons, "text", true);

            // move other option to the end of alphabetized list
            const otherOptionIndex = formattedReasons.findIndex(reason => reason.text === OTHER_OPTION);
            if (otherOptionIndex === -1) {
                return formattedReasons;
            } else {
                return formattedReasons.concat(formattedReasons.splice(otherOptionIndex, 1));
            }
        };

        const _showRemovePlanConfirmationModal = () => {
            return _promiseFactory.deferIndefinitely((promise) => {
                const constructorParams = [
                    self.billingPackage(),
                ];

                _actionModal.clearModal();
                _actionModal.setContentViewModel(RemovePlanConfirmationModalViewModelConstructor, constructorParams);
                _actionModal.setHeaderText({i18n: 'removePlan:areYouSure'});
                _actionModal.setSubmitButtonText({i18n: 'continue'});
                _actionModal.setOnSubmitCallback(self.submitAction);
                _actionModal.shouldDisplayCancelButton(true);
                _actionModal.shouldDisplayAgentBypassButton(false);
                _actionModal.shouldDisplaySubmitButton(true);
                _actionModal.shouldDisplayDefaultFooterMessage(true);

                _actionModal.showModal()
                    .done((result) => {
                        if (result === "cancel") {
                            _modalService.closeModal(_actionModal);
                        }
                        promise.resolve(result);
                    });
            });
        };

        self.modelStateObserver = null;
        self.billingPackage = ko.observable(null);
        self.subscriberType = ko.observable(null);
        self.subscriberId = ko.observable(null);
        self.plansUrl = _navigationConfiguration.routesById.plans.url;

        self.removePlanFeedbackReasons = ko.observableArray([]);
        self.tooExpensiveFeedbackReasons = ko.observableArray([]);
        self.noLongerNeededFeedbackReasons = ko.observableArray([]);
        self.ratingOptions = ko.observableArray([]);
        self.yesNoDropdownOptions = ko.observableArray(YES_NO_DROPDOWN_OPTIONS);
        self.removePlanButtonText = ko.pureComputed(() => _i18n.t('removePlan:removePlan'));
        self.pleaseSelectPlaceHolderMessage = _i18n.t('removePlan:pleaseSelect');

        self.removePlanFeedback = ko.observable("").extend({observeState: true});
        self.removePlanOtherFeedback = ko.observable("").extend({observeState: true});
        self.lacksFeaturesFeedback = ko.observable("").extend({observeState: true});
        self.billingIssueFeedback = ko.observable("").extend({observeState: true});
        self.technicalIssueFeedback = ko.observable("").extend({observeState: true});
        self.poorCustomerServiceFeedback = ko.observable("").extend({observeState: true});
        self.tooDifficultToUseFeedback = ko.observable("").extend({observeState: true});
        self.tooExpensiveFeedback = ko.observable("").extend({observeState: true});
        self.tooExpensiveOtherFeedback = ko.observable("").extend({observeState: true});
        self.noLongerNeededFeedback = ko.observable("").extend({observeState: true});
        self.noLongerNeededOtherFeedback = ko.observable("").extend({observeState: true});
        self.areYouSwitchingServices = ko.observable("").extend({observeState: true});
        self.whichService = ko.observable("").extend({observeState: true});
        self.howLikelyAreYouToRecommend = ko.observable().extend({observeState: true});
        self.additionalFeedback = ko.observable("").extend({observeState: true});

        self.headerI18n = ko.pureComputed(() => {
            if (!self.billingPackage()) {
                return "";
            }
            switch (self.billingPackage().type()) {
                case _billingPackageTypes.telephony.id:
                    return { key: 'removePlan:title', options: { type: _billingPackageTypes.telephony.name } };
                case _billingPackageTypes.liveReceptionist.id:
                    return { key: 'removePlan:title', options: { type: _billingPackageTypes.liveReceptionist.name } };
                case _billingPackageTypes.webChat.id:
                    return { key: 'removePlan:title', options: { type: _billingPackageTypes.webChat.name } };
                case _billingPackageTypes.outboundCalling.id:
                    return { key: 'removePlan:title', options: { type: _billingPackageTypes.outboundCalling.name } };
                default:
                    return "";
            }
        });

        self.billingPackageId = ko.pureComputed(() => {
            return self.billingPackage() ? self.billingPackage().billingPackageId : null;
        });
        self.billingPackageType = ko.pureComputed(() => {
            return self.billingPackage() ? self.billingPackage().type() : null;
        });

        self.showOtherFeedbackForm = ko.pureComputed(() => {
            return self.removePlanFeedback() === _removePlanFeedbackReasons.other.id;
        });
        self.showLacksFeaturesForm = ko.pureComputed(() => {
            return self.removePlanFeedback() === _removePlanFeedbackReasons.lacksFeatures.id;
        });
        self.showTooDifficultToUseForm = ko.pureComputed(() => {
            return self.removePlanFeedback() === _removePlanFeedbackReasons.tooDifficult.id;
        });
        self.showBillingIssueForm = ko.pureComputed(() => {
            return self.removePlanFeedback() === _removePlanFeedbackReasons.billingProblem.id;
        });
        self.showTechnicalIssueForm = ko.pureComputed(() => {
            return self.removePlanFeedback() === _removePlanFeedbackReasons.technicalProblem.id;
        });
        self.showPoorCustomerServiceForm = ko.pureComputed(() => {
            return self.removePlanFeedback() === _removePlanFeedbackReasons.poorCustomerService.id;
        });
        self.showTooExpensiveFeedbackForm = ko.pureComputed(() => {
            return self.removePlanFeedback() === _removePlanFeedbackReasons.tooExpensive.id;
        });
        self.showTooExpensiveOtherFeedback = ko.pureComputed(() => {
            return self.tooExpensiveFeedback() === _tooExpensiveReasons.other.id;
        });
        self.showNoLongerNeededFeedbackForm = ko.pureComputed(() => {
            return self.removePlanFeedback() === _removePlanFeedbackReasons.noLongerNeeded.id;
        });
        self.showNoLongerNeededOtherFeedback = ko.pureComputed(() => {
            return self.noLongerNeededFeedback() === _noLongerNeededReasons.other.id;
        });
        self.showWhichService = ko.pureComputed(() => {
            return self.areYouSwitchingServices() === DROPDOWN_OPTION_YES.id;
        });

        self.onRatingOptionClicked = (selectedOption) => {
            self.howLikelyAreYouToRecommend(selectedOption.id);
            self.ratingOptions().forEach((option) => {
                if (option.id === selectedOption.id) {
                    option.isSelected(true);
                } else {
                    option.isSelected(false);
                }
            });
        };

        self.getRemovePlanFeedback = () => {
            const removePlanFeedback = new RemovePlanFeedbackEntity();
            removePlanFeedback.billingPackageId = self.billingPackageId();
            removePlanFeedback.removePackageFeedback = self.removePlanFeedback();
            removePlanFeedback.removePackageOtherFeedback = self.removePlanOtherFeedback();
            removePlanFeedback.billingProblemFeedback = self.billingIssueFeedback();
            removePlanFeedback.lacksFeaturesFeedback = self.lacksFeaturesFeedback();
            removePlanFeedback.noLongerNeededFeedback = self.noLongerNeededFeedback();
            removePlanFeedback.noLongerNeededOtherFeedback = self.noLongerNeededOtherFeedback();
            removePlanFeedback.poorCustomerServiceFeedback = self.poorCustomerServiceFeedback();
            removePlanFeedback.technicalProblemFeedback = self.technicalIssueFeedback();
            removePlanFeedback.tooDifficultFeedback = self.tooDifficultToUseFeedback();
            removePlanFeedback.tooExpensiveFeedback = self.tooExpensiveFeedback();
            removePlanFeedback.tooExpensiveOtherFeedback = self.tooExpensiveOtherFeedback();
            removePlanFeedback.isSwitchingService = self.areYouSwitchingServices();
            removePlanFeedback.switchingServiceName = self.whichService();
            removePlanFeedback.howLikelyToRecommend = self.howLikelyAreYouToRecommend();
            removePlanFeedback.generalFeedback = self.additionalFeedback();
            return removePlanFeedback;
        };

        self.submitAction = () => {
            _promiseFactory.timeoutMilliSeconds = 60000;
            return _promiseFactory.deferWithMinimumWait((promise) => {
                self.modelStateObserver.saveData();

                const removePlanRequest = {
                    billingPackageId: self.billingPackageId(),
                    billingPackageType: self.billingPackageType(),
                    removePlanFeedback: self.getRemovePlanFeedback(),
                    sendAllCallsTo: {
                        subscriberType: self.subscriberType(),
                        subscriberId: self.subscriberId()
                    }
                };

                _facade.removePlan(removePlanRequest)
                    .fail(promise.reject)
                    .done(() => {
                        _router.navigate(self.plansUrl);
                        promise.resolve(true);
                    });
            });
        };

        self.cancel = () => {
            self.modelStateObserver.navigateWithoutWarning = true;
            _router.navigate(self.plansUrl);
        };

        self.validate = () => _validator.validate();

        self.save = () => {
            return _promiseFactory.deferWithMinimumWait((promise) => {
                _validator.validate()
                    .fail(promise.reject)
                    .done((isValid) => {
                        if (isValid === false) {
                            promise.resolve(false);
                            return;
                        }
                        _showRemovePlanConfirmationModal();
                        promise.resolve(true);
                    });
            });
        };

        self.activate = (billingPackageId, subscriberType, subscriberId) => {
            const FacadeConstructor = require('presentation/settings/billing/plans/facades/removePlanFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const ModalServiceConstructor = require('presentation/common/modal');
            _modalService = new ModalServiceConstructor();

            const ValidatorConstructor = require('presentation/settings/billing/plans/validators/removePlanValidator');
            _validator = new ValidatorConstructor();

            return _initialize(billingPackageId, subscriberType, subscriberId);
        };

        const _initialize = (billingPackageId, subscriberType, subscriberId) => {
            if (subscriberType){
                self.subscriberType(subscriberType);
            }
            if (subscriberId){
                self.subscriberId(subscriberId);
            }

            _facade.getBillingPackage(billingPackageId)
                .done((billingPackage) => {
                    if (billingPackage) {
                        self.billingPackage(billingPackage);
                    }
                });

            self.removePlanFeedbackReasons(_formatFeedbackReasons(_removePlanFeedbackReasons));
            self.tooExpensiveFeedbackReasons(_formatFeedbackReasons(_tooExpensiveReasons));
            self.noLongerNeededFeedbackReasons(_formatFeedbackReasons(_noLongerNeededReasons));
            self.ratingOptions(_buildRatingOptions());

            self.modelStateObserver = new ModelStateObserverConstructor(self, true);
            self.modelStateObserver.commitData();

            _validator.registerViewModel(self, _facade);
            
            return _promiseFactory.wait();
        };
    };
});
